<!--
This component is designed for use within the infinite-scrolling 'catalog' view. It only loads media that is actually "intersected" and it automatically unloads that media once it is no longer visible to facilitate fast scrolling. It also imposes a cap on the max number of simultaneous load requests to ensure that fast scrolling doesn't trigger a bunch of needless load calls.
-->

<template>
  <div v-intersect="onIntersect" class="overflow-hidden" style="aspect-ratio: 5 / 7;">
    <template v-if="show">
      <template v-if="!design.key">
        <card-preview v-if="full" :card="full" :is-card-owner="true" @loaded="end" />
      </template>

      <template v-if="isVideo">
        <!--
        Safari plays back mp4 sources inside picture / img elements. In low-power mode, iOS will not autoplay any videos, so this is a useful trick to work around that. It may better to simply use the webp version of the video in all cases (that's what Giphy appears to do), but the mp4 version is higher quality with better compression. Some discussion here: https://developer.apple.com/forums/thread/690523?page=2
        -->
        <picture v-if="IS_IOS_SAFARI">
          <source type="video/mp4" :srcset="base + design.key + '_270x378.mp4'">
          <source type="image/webp" :srcset="base + design.key + '_200x280.webp'">
          <img
            class="d-block loading-icon"
            v-bind:class="{ loaded: loadEnded }"
            width="100%" type="image/jpeg"
            :src="base + design.key + '_poster_270x378.jpg'"
            :alt="alt"
            @load="end"
            @error="end"
            @abort="end"
          />
        </picture>
        <video
          v-else
          class="d-block loading-icon"
          v-bind:class="{ loaded: loadEnded }"
          width="100%" playsinline autoplay muted loop
          :poster="base + design.key + '_poster_270x378.jpg'"
          :src="base + design.key + '_270x378.mp4'"
          :aria-label="alt"
          @loadeddata="end"
          @error="end"
          @abort="end"
        />
      </template>
      <img
        v-else
        class="d-block loading-icon"
        v-bind:class="{ loaded: loadEnded }"
        width="100%"
        :src="base + design.key + '_270x378.jpg'"
        :alt="alt"
        @load="end"
        @error="end"
        @abort="end"
      />
    </template>
  </div>
</template>

<script>
import CardPreview from '@/components/CardPreview.vue';
import { publicDesign } from '@/mixins/public-design.js';

// Note that the value of 'n' in the 'IN_PROGRESS' structure is shared by all instances of this component
const IN_PROGRESS = { n: 0 };
const MAX_IN_PROGRESS = 12;

export default {
  name: 'catalog-preview',

  mixins: [ publicDesign ],

  components: { CardPreview },

  props: [ 'design' ],

  data() {
    return {
      IN_PROGRESS,
      intersect: false,
      loadStarted: false,
      loadEnded: false,
      full: null,
      fullStatus: this.$track('getPublicDesign')
    }
  },

  computed: {
    isVideo() {
      return this.design.type == 'video/mp4';
    },

    show() {
      return (this.intersect || (this.loadStarted && !this.loadEnded)) && (this.loadStarted || (this.IN_PROGRESS.n < MAX_IN_PROGRESS));
    },

    alt() {
      return 'Ellacard card design' + (this.design.name ? ': ' + this.design.name : '');
    }
  },

  created() {
    this.base = 'https://cdn.ellacard.com/';
  },

  beforeDestroy() {
    if (!this.loadEnded)
      this.IN_PROGRESS.n--;
  },

  methods: {
    onIntersect(_1, _2, v) {
      this.intersect = v;
    },

    start() {
      this.loadStarted = true;
      this.IN_PROGRESS.n++;
      if (!this.design.key && !this.full && !this.fullStatus.pending)
        this.getPublicDesign(this.design.id).then(d => this.full = d)
    },

    end(e) {
      this.loadEnded = true;
      this.IN_PROGRESS.n--;
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(v) {
        if (v)
          this.start();
      }
    }
  }
}
</script>
